// These helpers are meant to be used by reducers that handle processing of standard QCS "sync" resources.

import {
  Map as ImmutableMap,
  OrderedSet,
  Record,
  type RecordOf,
  Set as SetImmutable,
  getIn,
  isIndexed,
  isKeyed,
  set,
} from 'immutable';
import _isEqualWith from 'lodash/isEqualWith';

import { getLogger } from '../../utils/core';

import type {
  BaseResource,
  QcsSyncResourceStore,
  QcsSyncResourceStoreProps,
  QcsSyncResourcesPayload,
} from './resourceStoreTypes';

const log = getLogger('flux/core/resourceUpserters.ts');

// mark resource as invalid and save the error
export function completeWithError<T extends BaseResource, V extends QcsSyncResourceStoreProps<T>>(
  state: QcsSyncResourceStore<T, V>,
  error: any,
) {
  return state.merge({
    error,
    isLoading: false,
  } as Partial<V>);
}

// soft reset - keeps valid records and force to update collection
export function resetResources<T extends BaseResource, V extends QcsSyncResourceStoreProps<T>>(
  state: QcsSyncResourceStore<T, V>,
  error: any,
) {
  return state.merge({
    resourcesById: state.resourcesById.filter((resource, key) => key !== resource.clientId || key === resource.id), // clean optimistic records only
    lastSyncDate: null,
    error,
  } as Partial<V>);
}

// reset to empty state - entire data collection need to be re-loaded from scratch
export function hardResetResources<T extends BaseResource>(state: QcsSyncResourceStore<T>, error: any) {
  return state.merge({
    resourcesById: ImmutableMap(),
    lastSyncDate: null,
    loadPending: true,
    error,
  });
}

// doUpsertResource
//
// Upsert a resource into the resourcesById map.
//
// Returns the updated resourcesById map.
//
function doUpsertResource<T extends BaseResource>(
  resourcesById: QcsSyncResourceStore<T>['resourcesById'],
  resourceToUpsert: RecordOf<T>,
  doMerge = true,
): ImmutableMap<string, any> {
  let updatedResourcesById = resourcesById;
  if (verify(!!resourceToUpsert, 'valid resourceToUpsert is required')) {
    const id = resourceToUpsert.id || resourceToUpsert.clientId;
    if (verify(!!id, 'doUpsertResource: no id found', resourceToUpsert) && id) {
      if (!resourceToUpsert.isDeleted) {
        const existingResource = resourcesById.get(id);
        if (!doMerge || !existingResource) {
          updatedResourcesById = resourcesById.set(id, resourceToUpsert);
        } else {
          updatedResourcesById = resourcesById.set(id, mergeDeepEx(existingResource, resourceToUpsert)!);
        }
      } else {
        updatedResourcesById = resourcesById.delete(id);
      }
      if (resourceToUpsert.id && resourceToUpsert.clientId) {
        // try and delete any resource by clientId once we update, safe to delete even if it doesn't exist
        updatedResourcesById = updatedResourcesById.delete(resourceToUpsert.clientId);
      }
    }
  }
  return updatedResourcesById;
}

type indexOrKey = string | number | symbol | undefined;

export type CompareFunc = ((val: any, val2: any, key: indexOrKey) => boolean | undefined) | string | undefined; // return true if equal

// by default, treat falsy values as equivalent and ignore 'modifiedAt'
const baseCompare = (val1: any, val2: any, key: indexOrKey) =>
  key === 'modifiedAt' || (!val1 && !val2) ? true : undefined;
// undefined lets lodash use its internal compare

// ================================================================================================
// upsertResource
//
// Optimized to upsert a single resource into the resources store. This method is generally called
// by the reducer after creating/updating/deleting a single resource. In this case, the default
// doMerge setting will doMerge in any changes from the payload sent to QCS. If this method is called
// for replacing a resource (after receiving from QCS), then doMerge should be set to false.
//
// Returns the updated state.
// ================================================================================================
export function upsertResource<T extends BaseResource, V extends QcsSyncResourceStoreProps<T>>(
  state: QcsSyncResourceStore<T, V>,
  resourceToUpsert: RecordOf<T>,
  doMerge = true,
  isLoading = false,
  comparator?: CompareFunc,
) {
  if (comparator && resourceToUpsert?.id) {
    const previousResource = state.resourcesById.get(resourceToUpsert.id);
    if (previousResource) {
      // allow using default modifiedAt ignore if 'default' string provided instead of function
      const finalComparator = typeof comparator === 'string' ? comparator === 'default' && baseCompare : comparator;
      if (finalComparator) {
        const prevJS = previousResource.toJS ? previousResource.toJS() : previousResource;
        const newJS = resourceToUpsert.toJS ? resourceToUpsert.toJS() : resourceToUpsert;
        if (_isEqualWith(prevJS, newJS, finalComparator)) {
          return state;
        }
      }
    }
  }
  return state.merge({
    resourcesById: doUpsertResource(state.resourcesById, resourceToUpsert, doMerge),
    isLoading,
  } as Partial<V>);
}

// upsertResources
//
// Optimized to upsert multiple sync resources into a sync resource store.
//
export function upsertResources<T extends BaseResource, V extends QcsSyncResourceStoreProps<T>>(
  state: QcsSyncResourceStore<T, V>,
  data: QcsSyncResourcesPayload<T>,
  comparator?: CompareFunc,
): QcsSyncResourceStore<T, V> {
  const { resources, doMerge = true, replaceCollection = false, lastSyncDate, error = null } = data;

  const resourcesById: ImmutableMap<string, RecordOf<T>> = replaceCollection ? ImmutableMap() : state.resourcesById;

  // optimize when upserting 0 or 1 resource
  //
  if (!resources || resources.length === 0) {
    return state.merge({
      resourcesById,
      loadPending: false,
      isLoading: false,
      error,
      lastSyncDate: lastSyncDate || state.lastSyncDate,
    } as Partial<V>);
  }
  if (comparator && resources.length <= 4) {
    const finalComparator = typeof comparator === 'string' ? comparator === 'default' && baseCompare : comparator;
    if (finalComparator) {
      // look for the first 'newData'
      const newDataIndex = resources.findIndex((resource) => {
        const prevResource = resource.id ? resourcesById.get(resource.id) : undefined;
        if (!prevResource) return true;
        const prevJS = prevResource.toJS ? prevResource.toJS() : prevResource;
        const newJS = resource.toJS ? resource.toJS() : resource;
        return !_isEqualWith(prevJS, newJS, finalComparator);
      });

      if (newDataIndex === -1) {
        // did not find any new data
        return state.merge({
          resourcesById, // keeping existing resources
          loadPending: false,
          isLoading: false,
          error,
          lastSyncDate: lastSyncDate || state.lastSyncDate,
        } as Partial<V>);
      }
    }
  }

  if (resources.length === 1) {
    return state.merge({
      resourcesById: doUpsertResource(resourcesById, resources[0], doMerge),
      loadPending: false,
      isLoading: false,
      error,
      lastSyncDate: lastSyncDate || state.lastSyncDate,
    } as Partial<V>);
  }

  return state.merge({
    resourcesById: resourcesById.withMutations((resourcesByIdMutable) => {
      resources.forEach((resource) => {
        doUpsertResource(resourcesByIdMutable, resource, doMerge);
      });
    }),
    loadPending: false,
    isLoading: false,
    error,
    lastSyncDate: lastSyncDate || state.lastSyncDate,
  } as Partial<V>);
}

type SimpleObject = { [key: string]: any };

function resolveConflictAndContinue(
  curObj: SimpleObject,
  key: string,
  value: any,
  newObj: SimpleObject,
  basePath: string[],
) {
  let retObj = curObj;

  const path = [...basePath, key];
  if (Array.isArray(value) || isIndexed(value) || SetImmutable.isSet(value) || OrderedSet.isOrderedSet(value)) {
    // erase arrays or simple collections
    const newValue = getIn(newObj, path);
    if (newValue) {
      try {
        retObj = set(retObj, key, null);
      } catch (e) {
        log.error(e);
      }
    }
  } else if (typeof value === 'object' && value !== null && key) {
    const retValue = resolveConflicts(value, newObj, path);
    try {
      retObj = set(retObj, key, retValue);
    } catch (e) {
      log.error(e);
    }
  }

  return retObj;
}

function resolveConflicts(curObj: any, newObj: any, basePath?: string[]) {
  let retObj = curObj;

  if ((Record.isRecord(curObj) || isKeyed(curObj)) && curObj[Symbol.iterator]) {
    // iterate over keyed immutables
    for (const prop of curObj) {
      if (Array.isArray(prop) && prop.length === 2) {
        const [key, value] = prop;
        retObj = resolveConflictAndContinue(retObj, key as string, value, newObj, basePath || []);
      }
    }
  } else if (typeof curObj === 'object' && curObj !== null) {
    // iterate over js objects
    for (const key in curObj) {
      const value = curObj[key];
      retObj = resolveConflictAndContinue(retObj, key, value, newObj, basePath || []);
    }
  }

  return retObj;
}

export function mergeDeepEx<T>(oldObj: T, newObj: T): T | undefined {
  const oldObjResolved = resolveConflicts(oldObj, newObj);
  let mergedObj;
  if (Record.isRecord(newObj)) {
    // if new record, merge into new record in case record types/fields updated
    mergedObj = newObj.mergeDeepWith(
      (newValue, oldValue) => (newValue === undefined ? oldValue : newValue),
      oldObjResolved,
    );
  } else if ('mergeDeepWith' in oldObjResolved) {
    // merge JS obj into current record
    mergedObj = oldObjResolved.mergeDeepWith(
      (oldValue: any, newValue: any) => (newValue === undefined ? oldValue : newValue),
      newObj as { [key: string]: unknown },
    ) as T;
  }
  return mergedObj;
}
